/**
 * Drill-down helpers for analytics history charts.
 * Recalculates Flux aggregation interval for a user-selected time range.
 */

export function toFluxTime(ms) {
  const date = new Date(ms)
  if (Number.isNaN(date.getTime())) {
    return null
  }
  // Flux accepts RFC3339 timestamps unquoted in range(start, stop)
  return date.toISOString().replace(/\.\d{3}Z$/, 'Z')
}

export function toEpochMs(value) {
  if (value == null) {
    return null
  }
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return value
  }
  const ms = new Date(value).getTime()
  return Number.isNaN(ms) ? null : ms
}

/**
 * Choose an aggregation window so the selected span yields a readable
 * number of points (roughly 60–120 for typical ranges).
 */
export function calculateAggregationInterval(startMs, endMs) {
  const durationMs = Math.max((endMs || 0) - (startMs || 0), 60_000)
  const minute = 60_000
  const hour = 60 * minute
  const day = 24 * hour

  if (durationMs <= 2 * hour) return '1m'
  if (durationMs <= 12 * hour) return '5m'
  if (durationMs <= 2 * day) return '15m'
  if (durationMs <= 7 * day) return '1h'
  if (durationMs <= 31 * day) return '6h'
  if (durationMs <= 90 * day) return '1d'
  if (durationMs <= 366 * day) return '1w'
  return '1mo'
}

export function buildDrillDownRange(startValue, endValue) {
  const startMs = toEpochMs(startValue)
  const endMs = toEpochMs(endValue)
  if (startMs == null || endMs == null || endMs <= startMs) {
    return null
  }

  return {
    startMs,
    endMs,
    start: toFluxTime(startMs),
    stop: toFluxTime(endMs),
    interval: calculateAggregationInterval(startMs, endMs),
  }
}

const SYMBOL_SPACING_PX = 55
const OVERLAPPING_SYMBOL_SPACING_PX = 78
const FALLBACK_CHART_WIDTH_PX = 900

/**
 * Show discrete markers on line series. Markers are thinned to keep a readable
 * gap between them: spacing is derived from the rendered chart width and is
 * widened further when several series overlap on the same x values.
 */
export function applyDataPointSymbols(option, isDrillDown = false, chartWidth = 0) {
  if (!option?.series?.length) {
    return option
  }

  const lineSeries = option.series.filter((series) => series && series.type !== 'gauge' && series.type !== 'bar')
  if (!lineSeries.length) {
    return option
  }

  const width = chartWidth > 0 ? chartWidth : FALLBACK_CHART_WIDTH_PX
  const spacing = lineSeries.length >= 3 ? OVERLAPPING_SYMBOL_SPACING_PX : SYMBOL_SPACING_PX
  const maxVisiblePoints = Math.min(28, Math.max(6, Math.floor(width / spacing)))

  const pointCount = lineSeries.reduce((max, series) => Math.max(max, Array.isArray(series.data) ? series.data.length : 0), 0)
  // One step for every series so markers stay vertically aligned.
  const step = pointCount > maxVisiblePoints ? Math.ceil(pointCount / maxVisiblePoints) : 1
  const lastIndex = pointCount - 1

  lineSeries.forEach((series) => {
    series.showSymbol = true
    series.symbolSize = isDrillDown ? 7 : 6
    series.symbol = (value, params) => {
      const index = params?.dataIndex ?? 0
      return index % step === 0 || index === lastIndex ? 'circle' : 'none'
    }
    series.emphasis = {
      ...(series.emphasis || {}),
      scale: true,
      focus: 'series',
    }
  })

  return option
}

/**
 * Remove leftover tooltip and axis-pointer crosshairs. ECharts keeps the last
 * axis pointer alive across setOption, so repeated drag-selections otherwise
 * leave stale dashed lines and value labels on the canvas.
 */
export function clearChartHoverState(chart) {
  if (!chart || chart.isDisposed?.()) {
    return
  }

  chart.dispatchAction({ type: 'hideTip' })
  chart.dispatchAction({ type: 'updateAxisPointer', currTrigger: 'leave' })
  chart.dispatchAction({ type: 'downplay' })

  const zr = chart.getZr()
  if (zr && zr.handler) {
    zr.handler.dispatch('globalout', {})
    zr.handler.dispatch('mouseout', {})
  }
}

/**
 * Suspend/resume tooltip interaction without removing the tooltip component.
 * Toggling `tooltip.show` tears the component down while its crosshair is on
 * screen, which strands the rendered lines and labels on the canvas.
 */
export function setTooltipInteraction(chart, enabled) {
  if (!chart || chart.isDisposed?.()) {
    return
  }

  if (!enabled) {
    clearChartHoverState(chart)
  }

  chart.setOption({ tooltip: { triggerOn: tooltipTriggerOn(enabled) } })

  if (!enabled) {
    clearChartHoverState(chart)
  }
}

export function tooltipTriggerOn(enabled) {
  return enabled ? 'mousemove|click' : 'none'
}

export const DRILL_DOWN_LOADING = {
  text: 'Loading data, please wait...',
  color: '#4f6bed',
  textColor: '#000',
  maskColor: 'rgba(255, 255, 255, 0.2)',
  zlevel: 0,
}
